import { z } from 'zod'
import { MessageId, ThreadId, FieldId, SectionId } from './id.js'
import { Moment, Person, compareMoments } from './moment.js'

/**
 * Een opmerkingdraadje bij een veld (R-73, ontwerp 6.8).
 *
 * R-73 vraagt een opmerking bij een veld tussen de mensen die samen aan het formulier
 * werken. Dat is niet één tekst maar een gesprek: iemand plaatst een opmerking, een ander
 * antwoordt, en op een moment is de zaak afgedaan. De vorm hieronder komt uit de CitatenPOC,
 * waar dit met NWO is afgestemd (eisenboek 12.9), met drie wijzigingen die overal doorwerken
 * en die hier staan omdat ze de vorm bepalen.
 *
 * **1. De auteur komt uit het token en niet uit de client.** In de POC schrijft de client
 * zijn eigen naam en kleur in het draadje. Hier is `persoon` een `Persoon`, dus een
 * tokensubject met zijn gastheer ervoor, en de pod stempelt hem net als bij een veldwaarde
 * (`herkomst.ts` in M3). Een door de client opgegeven auteur wordt genegeerd: wie een
 * opmerking op naam van iemand anders kan zetten, kan een gesprek verzinnen.
 *
 * **2. Er is geen kleurveld.** In de POC staat de auteurkleur in elk bericht. Dat is
 * afgeleide gegevens in primaire opslag: twee berichten van dezelfde persoon kunnen dan
 * verschillende kleuren dragen, en een kleur die eenmaal is weggeschreven verandert niet mee
 * met een palet. Kleur wordt deterministisch uit `persoon` berekend, in de renderer, en
 * nooit bewaard. Bovendien is kleur nooit de enige drager van informatie (R-139): de naam
 * staat er als tekst bij.
 *
 * **3. Momenten zijn ISO 8601 met een Z en geen millisecondengetal.** Dezelfde vorm als de
 * rest van het model (ontwerp 4.5), want een revisie legt deze inhoud mee vast en twee
 * tijdvormen in één document zijn er één te veel.
 *
 * **Waar het draadje leeft.** In het Yjs-sectiedocument, onder een eigen top-level map, en
 * niet in een tabel: het is samenwerkinhoud die op tekenniveau meebeweegt met de tekst
 * (R-72). De verankering zit als mark in de tekst en draagt alleen het draad-id, zodat een
 * anker meeschuift als er ervoor wordt getypt. Wat hier staat is de vorm van die map, en dat
 * is dezelfde soort afspraak als de vorm van een rich-text-veld in 6.8.
 *
 * **Waarom `citaat` erbij staat.** Het anker kan verdwijnen: wie de tekst waar een opmerking
 * aan hangt weggooit, gooit de mark mee weg. Het draadje mag daar niet mee verdwijnen, want
 * dan is een gesprek weg zonder dat iemand het heeft afgerond. Het citaat is de momentopname
 * van de tekst waarop de opmerking is gemaakt, zodat het draadje ook zonder anker leesbaar
 * blijft. Het is met opzet een kopie en geen verwijzing: een verwijzing naar tekst die er
 * niet meer is levert niets op.
 */

/** Eén bericht in een draadje. De auteur is een tokensubject en geen naam. */
export const Message = z.strictObject({
  id: MessageId,
  /** Wie het schreef, uit het token (R-154, R-155). Nooit uit de invoer van de client. */
  persoon: Person,
  tekst: z.string().min(1),
  moment: Moment,
})
export type Message = z.infer<typeof Message>

export const Thread = z
  .strictObject({
    id: ThreadId,
    /**
     * Het veld waarin het anker leeft.
     *
     * Op veldniveau en niet op sectieniveau, want R-73 zegt "bij een veld of onderdeel". Het
     * paneel groepeert erop en de renderer weet waar hij het draadje moet plaatsen. Binnen
     * het veld houdt de mark in de tekst de precieze plek vast, en die staat hier niet: een
     * tekenpositie in primaire opslag zou verouderen bij de eerste wijziging.
     */
    veldId: FieldId.optional(),
    /**
     * De sectie, voor een opmerking bij de sectie als geheel (R-73: "bij een veld of onderdeel",
     * besluit 8 van 30 oktober 2026). Een draadje hangt bij precies één van de twee: een veld of een
     * sectie. Additief: een draadje van vóór dit veld heeft een veld en geen sectie, en blijft geldig.
     * Het draadje leeft in het document van die sectie; dit veld zegt dat het bij de kop hoort en
     * niet bij een veld.
     *
     * **Een anker zonder tekstbereik** heeft geen mark in de tekst: een opmerking bij een sectie of bij
     * een veld dat geen rich text is, of bij een rich-text-veld als geheel. Het citaat is dan leeg. Er
     * komt daarvoor geen mark en geen node in het schema van de editor (valkuil 2 en 3).
     */
    sectieId: SectionId.optional(),
    /** De tekst waarop de opmerking is gemaakt, voor als het anker verdwijnt; leeg zonder tekstbereik. */
    citaat: z.string(),
    /**
     * Afgedaan of niet.
     *
     * Een opgelost draadje houdt al zijn berichten: het gesprek is het bewijs waarom er iets
     * is gewijzigd. Bij een compactie blijven open draadjes mee en gaan opgeloste met de
     * gearchiveerde generatie (ontwerp 4.6). Verder verdwijnt een bericht alleen als zijn
     * schrijver het verwijdert (eisenboek 12.45, `messageDeletableBy`); een revisie houdt wat
     * ze bij de overdracht bevroor.
     */
    opgelost: z.boolean(),
    /**
     * Wie het draadje oploste en wanneer, uit het token en van de klok van de pod (ontwerp C.3, besluit
     * van 2 november 2026, eisenboek 12.46). Alleen bij een opgelost draadje, en dan samen. Additief: een
     * draadje dat is opgelost vóór dit veld heeft geen van beide en blijft geldig; de kaart zegt dan
     * alleen dat het is opgelost. De client zet ze nooit; de pod zet ze nadat de stempel in het
     * stempelregister van de opslag staat (`apps/runtime/src/threads.ts`).
     */
    opgelostDoor: Person.optional(),
    opgelostOp: Moment.optional(),
    /**
     * De berichten, oudste eerst.
     *
     * Minstens één: een draadje zonder bericht is geen gesprek maar een lege markering, en
     * die hoort niet te bestaan. Het eerste bericht is de opmerking zelf; wie het draadje
     * begon staat er dus in en heeft geen eigen veld nodig.
     */
    berichten: z.array(Message).min(1),
  })
  .superRefine((thread, ctx) => {
    if ((thread.opgelostDoor === undefined) !== (thread.opgelostOp === undefined)) {
      ctx.addIssue({ code: 'custom', path: ['opgelostDoor'], message: 'wie het draadje oploste en wanneer horen bij elkaar' })
    }
    if (thread.opgelostDoor !== undefined && !thread.opgelost) {
      // Een heropend draadje is niet opgelost; wie het eerder oploste staat in het stempelregister.
      ctx.addIssue({ code: 'custom', path: ['opgelostDoor'], message: 'alleen een opgelost draadje heeft een oplosser' })
    }
    if ((thread.veldId === undefined) === (thread.sectieId === undefined)) {
      // Precies één: een draadje bij niets is niet te plaatsen, en een bij twee dingen is niet te
      // zeggen waar het hoort.
      ctx.addIssue({ code: 'custom', path: ['veldId'], message: 'een draadje hangt bij een veld of bij een sectie, en bij precies één' })
    }
    const ids = new Set<string>()
    for (const [place, message] of thread.berichten.entries()) {
      if (ids.has(message.id)) {
        // Twee berichten met hetzelfde id maken een reactie niet meer aanwijsbaar, en in een
        // CRDT is dat precies het geval waar twee clients tegelijk hebben geschreven.
        ctx.addIssue({
          code: 'custom',
          path: ['berichten', place, 'id'],
          message: `bericht ${message.id} staat twee keer in dit draadje`,
        })
      }
      ids.add(message.id)
    }
    for (let place = 1; place < thread.berichten.length; place++) {
      const previous = thread.berichten[place - 1]
      const current = thread.berichten[place]
      if (previous !== undefined && current !== undefined && compareMoments(current.moment, previous.moment) < 0) {
        // Oudste eerst, want het paneel toont ze in deze volgorde en een gesprek dat door
        // elkaar staat is niet te volgen. Sorteren bij het lezen zou het ook oplossen; het
        // hier weigeren houdt de fout bij wie hem maakt.
        ctx.addIssue({
          code: 'custom',
          path: ['berichten', place, 'moment'],
          message: 'de berichten van een draadje staan op moment, oudste eerst',
        })
      }
    }
  })
export type Thread = z.infer<typeof Thread>

/**
 * Wie een draadje begon: de auteur van het eerste bericht.
 *
 * Afgeleid en niet opgeslagen, want het staat er al. Een eigen veld zou kunnen afwijken van
 * het eerste bericht, en dan is niet te zeggen welke van de twee waar is.
 */
export function startedBy(thread: Thread): Person {
  const first = thread.berichten[0]
  // Het schema eist minstens één bericht; dit is de vangnetregel die TypeScript vraagt.
  if (first === undefined) throw new Error('een draadje zonder berichten bestaat niet')
  return first.persoon
}

/** Wanneer een draadje begon. Zelfde reden als `startedBy`. */
export function startedAt(thread: Thread): Moment {
  const first = thread.berichten[0]
  if (first === undefined) throw new Error('een draadje zonder berichten bestaat niet')
  return first.moment
}

/**
 * Mag deze persoon dit bericht verwijderen? Alleen als hij het zelf schreef (besluit van 2 november
 * 2026, eisenboek 12.45). Het bericht van een ander blijft staan; het draadje kan wel worden opgelost.
 *
 * Een eigen functie en geen vergelijking op de plek van gebruik: de renderer toont de knop met deze
 * regel en de pod dwingt dezelfde regel af (`apps/runtime/src/threadGuard.ts`).
 */
export function messageDeletableBy(message: Message, person: Person): boolean {
  return message.persoon === person
}

/**
 * Mag deze persoon het hele draadje verwijderen? Alleen als hij het begon en niemand anders erin
 * schreef: elk bericht is van hem (eisenboek 12.45).
 */
export function threadDeletableBy(thread: Thread, person: Person): boolean {
  return thread.berichten.every((message) => messageDeletableBy(message, person))
}
